import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import MKButton from './MKButton';
import API_BASE_URL from '../apiConfig';
import {
  PRIMARY_PINK,
  PRIMARY_PINK_DARK,
  SUCCESS_GREEN,
  WARNING_ORANGE,
} from '../constants';

function USCFSectionsManager({ eventId }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [form, setForm] = useState({
    name: '',
    min_rating: '',
    max_rating: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (eventId) {
      fetchSections();
    }
  }, [eventId]);

  const fetchSections = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/uscf-sections/${eventId}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setSections(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch sections');
      }
    } catch (error) {
      console.error('Error fetching sections:', error);
      setError('Failed to fetch sections');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (section = null) => {
    setEditingSection(section);
    setForm({
      name: section?.name || '',
      min_rating: section?.min_rating || '',
      max_rating: section?.max_rating || '',
    });
    setOpenDialog(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSection(null);
    setForm({ name: '', min_rating: '', max_rating: '' });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const url = editingSection
        ? `${API_BASE_URL}/api/uscf-sections/${editingSection.id}`
        : `${API_BASE_URL}/api/uscf-sections/${eventId}`;
      
      const method = editingSection ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: form.name.trim(),
          min_rating: form.min_rating ? parseInt(form.min_rating) : null,
          max_rating: form.max_rating ? parseInt(form.max_rating) : null,
        }),
      });

      if (response.ok) {
        setSuccess(editingSection ? 'Section updated successfully!' : 'Section created successfully!');
        handleCloseDialog();
        fetchSections();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save section');
      }
    } catch (error) {
      console.error('Error saving section:', error);
      setError('Failed to save section');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/uscf-sections/${sectionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setSuccess('Section deleted successfully!');
        fetchSections();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete section');
      }
    } catch (error) {
      console.error('Error deleting section:', error);
      setError('Failed to delete section');
    }
  };

  const formatRatingRange = (min, max) => {
    if (min && max) {
      return `${min} - ${max}`;
    } else if (min) {
      return `${min}+`;
    } else if (max) {
      return `Up to ${max}`;
    } else {
      return 'Any rating';
    }
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: PRIMARY_PINK }}>
              USCF Sections
            </Typography>
            <MKButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: PRIMARY_PINK,
                color: '#ffffff',
                '&:hover': {
                  bgcolor: PRIMARY_PINK_DARK,
                },
              }}
            >
              Add Section
            </MKButton>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {sections.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No sections configured. Add sections to organize players by rating.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {sections.map((section) => (
                <Box
                  key={section.id}
                  sx={{
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {section.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Rating range: {formatRatingRange(section.min_rating, section.max_rating)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(section)}
                      sx={{ color: PRIMARY_PINK }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(section.id)}
                      sx={{ color: WARNING_ORANGE }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Section Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSection ? 'Edit Section' : 'Add New Section'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              autoFocus
              margin="dense"
              required
              fullWidth
              label="Section Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Open, U1800, U1400"
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                margin="dense"
                fullWidth
                label="Minimum Rating"
                type="number"
                value={form.min_rating}
                onChange={(e) => setForm({ ...form, min_rating: e.target.value })}
                placeholder="e.g., 1800"
                helperText="Leave empty for no minimum"
              />
              <TextField
                margin="dense"
                fullWidth
                label="Maximum Rating"
                type="number"
                value={form.max_rating}
                onChange={(e) => setForm({ ...form, max_rating: e.target.value })}
                placeholder="e.g., 2199"
                helperText="Leave empty for no maximum"
              />
            </Box>

            <Typography variant="body2" color="text.secondary">
              Players will be automatically placed in the appropriate section based on their USCF rating.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={saving}>
              Cancel
            </Button>
            <MKButton
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{
                bgcolor: PRIMARY_PINK,
                color: '#ffffff',
                '&:hover': {
                  bgcolor: PRIMARY_PINK_DARK,
                },
              }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : (editingSection ? 'Update' : 'Create')}
            </MKButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

export default USCFSectionsManager;
















